import {
  faCircleQuestion,
  faEdit,
  faNoteSticky,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  AdvancedSelect,
  Button,
  Input,
  Loading,
  LoadingBlock,
  Table,
  TableColumnHeader,
  TableColumnInstance,
} from '@orobix/orobix-fe-components'
import { dissoc } from 'ramda'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroller'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import DeleteConfirmationModal from '../../components/DeleteConfirmationModal/DeleteConfirmationModal'
import FiltersModal from '../../components/FiltersModal/FiltersModal'
import HtmlModal from '../../components/HtmlModal/HtmlModal'
import InfiniteListWrapper from '../../components/InfiniteListWrapper/InfiniteListWrapper'
import PrecisionAlertModal from '../../components/PrecisionAlertModal/PrecisionAlertModal'
import useSettings from '../../components/Settings/useSettings'
import { SortButton } from '../../components/SortButton/SortButton'
import { useDispatch, useStore } from '../../store/store'
import { Training } from '../../store/store.types'
import {
  abortTraining,
  deleteTraining,
  emptyTrainings,
  fetchTrainings,
  setTraining,
} from '../../store/storeActions'
import { Sorting, SortingDirection } from '../../types/sorting'
import { formatDate } from '../../utils/date'
import { isProductEnabled } from '../../utils/productTypes'
import { serializeSorting } from '../../utils/sorting'
import {
  getAccuracyName,
  getTrainingStatusNames,
  toScore,
} from '../../utils/trainings'
import DownloadButton from './DownloadButton/DownloadButton'
import DownloadConfirmationModal from './DownloadConfirmationModal/DownloadConfirmationModal'
import useStyles from './TrainingsList.styles'
import TrainingsUpdate from './TrainingsUpdate'
import { downloadTrainingFile } from './utils'

interface RouteParams {
  productId: string
  projectId: string
}

interface TrainingListProps {
  selectedTrainingId?: number
  setDetailsRefreshTrigger?: React.Dispatch<React.SetStateAction<number>>
}

const itemsPerPage = 50

interface TrainingColumns extends Training {
  actions?: string
}

type TrainingFilters = {
  name?: string
  status?: string
  backbone?: string
}

const TrainingList = ({
  selectedTrainingId,
  setDetailsRefreshTrigger,
}: TrainingListProps) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const history = useHistory()
  const { trainings, product } = useStore()
  const dispatch = useDispatch()
  const [selectedTraining, setSelectedTraining] = useState<TrainingColumns>()
  const [isDeleteSubmitting, setDeleteSubmitting] = useState(false)
  const [isAbortSubmitting, setAbortSubmitting] = useState(false)
  const [deleteConfirmationModal, setDeleteConfirmationModal] = useState(false)
  const [downloadConfirmationModal, setDownloadConfirmationModal] = useState(
    false
  )
  const [trainingsUpdateModal, setTrainingsUpdateModal] = useState(false)
  const [precisionAlertModal, setPrecisionAlertModal] = useState(false)

  const { productId } = useParams<RouteParams>()
  const [htmlHelperModal, setHtmlHelperModal] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const abortControllerRef = useRef<AbortController>()

  const { settings } = useSettings()
  const [preferenceShowAlert, setPreferenceShowAlert] = useState(false)

  const [sorting, setSorting] = useState<Sorting>({})
  const sortingRef = useRef<Sorting>({})
  const [trainingFilters, setTrainingFilters] = useState<TrainingFilters>({})
  const trainingFiltersRef = useRef<TrainingFilters>({})
  const [listKey, setListKey] = useState(0)

  useEffect(() => {
    if (settings?.preference__show_precision_alert_on_download) {
      setPreferenceShowAlert(true)
    }
  }, [settings])

  const serializeFilters = (f: TrainingFilters): string => {
    const parts: string[] = []
    if (f.name) parts.push(`name=${encodeURIComponent(f.name)}`)
    if (f.status) parts.push(`status=${encodeURIComponent(f.status)}`)
    if (f.backbone) parts.push(`backbone=${encodeURIComponent(f.backbone)}`)
    return parts.length ? `&${parts.join('&')}` : ''
  }

  const resetList = () => {
    abortControllerRef.current?.abort()
    setIsLoading(false)
    emptyTrainings(dispatch)
    setListKey((k) => k + 1)
  }

  const handleLoadMore = (page: number) => {
    if (isLoading) return
    setIsLoading(true)
    abortControllerRef.current = new AbortController()
    const orderingParam = serializeSorting(sortingRef.current) || undefined
    const filterParam =
      serializeFilters(trainingFiltersRef.current) || undefined
    fetchTrainings(
      dispatch,
      page !== 0,
      Number(productId),
      itemsPerPage,
      page * itemsPerPage,
      orderingParam,
      filterParam,
      abortControllerRef.current.signal
    )?.finally(() => setIsLoading(false))
  }

  const handleSort = (key: string, direction: SortingDirection) => {
    const newSorting = { ...sortingRef.current, [key]: direction }
    sortingRef.current = newSorting
    setSorting(newSorting)
    resetList()
  }

  const handleSaveFilters = (f: Partial<TrainingFilters>) => {
    trainingFiltersRef.current = f as TrainingFilters
    setTrainingFilters(f as TrainingFilters)
    resetList()
  }

  const handleRemoveFilters = () => {
    trainingFiltersRef.current = {}
    setTrainingFilters({})
    resetList()
  }

  const filterLabels: { [key in keyof TrainingFilters]: string } = {
    name: t('Name'),
    status: t('Status'),
    backbone: t('Backbone'),
  }

  const doDownloadTrainingFile = useCallback(
    (trainingId: number) => {
      toast.success(t('Your download will start soon.'))
      downloadTrainingFile(trainingId)
    },
    [t]
  )

  useEffect(
    () => () => {
      abortControllerRef.current?.abort()
      emptyTrainings(dispatch)
    },
    [dispatch]
  )

  const statusOptions = [
    { value: 'IDLE', label: getTrainingStatusNames('IDLE') },
    { value: 'RUNNING', label: getTrainingStatusNames('RUNNING') },
    { value: 'COMPLETED', label: getTrainingStatusNames('COMPLETED') },
    { value: 'ERROR', label: getTrainingStatusNames('ERROR') },
    { value: 'CANCELED', label: getTrainingStatusNames('CANCELED') },
  ]

  const columns: TableColumnInstance<
    TrainingColumns,
    keyof TrainingColumns
  >[] = [
    {
      id: 'id',
      header: t('Id'),
      template: ({ id }) => `#${id}`,
    },
    {
      id: 'name',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Name')}
            sorting={
              <SortButton
                sort={sorting?.name}
                onSort={(direction) => handleSort('name', direction)}
              />
            }
          />
        </div>
      ),
      template: useCallback(
        ({ name, notes }) => (
          <>
            {name}{' '}
            {notes && <FontAwesomeIcon icon={faNoteSticky} title={notes} />}
          </>
        ),
        []
      ),
    },
    {
      id: 'backbone',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Backbone')}
            sorting={
              <SortButton
                sort={sorting?.backbone}
                onSort={(direction) => handleSort('backbone', direction)}
              />
            }
          />
        </div>
      ),
      template: ({ backbone }) => backbone.name,
    },
    {
      id: 'status',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Status')}
            sorting={
              <SortButton
                sort={sorting?.status}
                onSort={(direction) => handleSort('status', direction)}
              />
            }
          />
        </div>
      ),
      template: useCallback(
        ({ status }) => (
          <>
            {getTrainingStatusNames(status)}
            {status === 'RUNNING' && (
              <>
                {' '}
                <Loading />
              </>
            )}
          </>
        ),
        []
      ),
    },
    {
      id: 'overallPerformance',
      header: (
        <div>
          <TableColumnHeader
            title={product ? getAccuracyName(product.type) : ''}
            info={
              product && (
                <FontAwesomeIcon
                  icon={faCircleQuestion}
                  onClick={() => setHtmlHelperModal(true)}
                />
              )
            }
          />
        </div>
      ),
      template: ({ overallPerformance }) =>
        overallPerformance ? toScore(overallPerformance.data.value) : '-',
    },
    { id: 'createdBy', header: t('Created by') },
    {
      id: 'creationDate',
      header: (
        <div className={classes.sortableColumn}>
          <TableColumnHeader
            title={t('Created on')}
            sorting={
              <SortButton
                sort={sorting?.creationDate}
                onSort={(direction) => handleSort('creationDate', direction)}
              />
            }
          />
        </div>
      ),
      template: ({ creationDate }) => formatDate(creationDate),
    },
    {
      id: 'actions',
      header: t('Actions'),
      noWrap: true,
      align: 'right',
      width: 0,
      template: useCallback(
        (tr) => {
          const canDownload =
            isProductEnabled(product?.type) &&
            tr.outputFileStatus === 'AVAILABLE' &&
            !tr.backbone.isDemo

          return (
            <>
              {(tr.status === 'IDLE' || tr.status === 'RUNNING') && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    abortTraining(dispatch, tr.id, setAbortSubmitting)
                  }}
                  disabled={isAbortSubmitting}
                  label={t('Abort')}
                  buttonType="button"
                  type="default"
                  size="sm"
                />
              )}

              {tr.status === 'COMPLETED' && (
                <DownloadButton
                  canDownload={canDownload}
                  isDemo={tr.backbone.isDemo}
                  showPrecisionAlertModal={() => {
                    if (
                      tr.metadata?.precision === 'fp16' &&
                      preferenceShowAlert
                    ) {
                      setPrecisionAlertModal(true)
                    }
                  }}
                  onDirectDownload={() => doDownloadTrainingFile(tr.id)}
                  onRequiresConfirmation={() => {
                    setSelectedTraining(tr)
                    setDownloadConfirmationModal(true)
                  }}
                />
              )}

              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  setSelectedTraining(tr)
                  setTrainingsUpdateModal(true)
                }}
                label={t('Edit')}
                hideLabel
                icon={<FontAwesomeIcon icon={faEdit} />}
                buttonType="button"
                type="blank-default"
              />

              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  setSelectedTraining(tr)
                  setDeleteConfirmationModal(true)
                }}
                label={t('Delete')}
                hideLabel
                icon={<FontAwesomeIcon icon={faTrash} />}
                disabled={isDeleteSubmitting}
                buttonType="button"
                type="blank-destroy"
                size="sm"
              />
            </>
          )
        },
        [
          product?.type,
          isAbortSubmitting,
          t,
          isDeleteSubmitting,
          dispatch,
          preferenceShowAlert,
          doDownloadTrainingFile,
        ]
      ),
    },
  ]

  return (
    <>
      <div className={classes.header}>
        <FiltersModal<TrainingFilters>
          initialValues={trainingFilters}
          labels={filterLabels}
          removeDisabled
          onSave={handleSaveFilters}
          onRemove={handleRemoveFilters}
        >
          {({ filters: f, setFilters }) => (
            <>
              <Input
                name="nameFilter"
                id="nameFilter"
                type="text"
                label={t('Name')}
                placeholder={t('Filter...')}
                value={f.name || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFilters((prev) => ({
                    ...prev,
                    name: e.target.value || undefined,
                  }))
                }
              />
              <AdvancedSelect
                name="statusFilter"
                id="statusFilter"
                isClearable
                label={t('Status')}
                placeholder={t('Filter...')}
                value={
                  f.status
                    ? statusOptions.find((o) => o.value === f.status) ?? null
                    : null
                }
                onChange={(v: { value: string; label: string } | null) =>
                  setFilters((prev) => ({
                    ...prev,
                    status: v ? v.value : undefined,
                  }))
                }
                options={statusOptions}
              />
              <Input
                name="backboneFilter"
                id="backboneFilter"
                type="text"
                label={t('Backbone')}
                placeholder={t('Filter...')}
                value={f.backbone || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFilters((prev) => ({
                    ...prev,
                    backbone: e.target.value || undefined,
                  }))
                }
              />
            </>
          )}
        </FiltersModal>
      </div>
      <InfiniteListWrapper>
        {({ stickedHeader }) => (
          <InfiniteScroll
            key={listKey}
            pageStart={-1}
            loadMore={handleLoadMore}
            hasMore={
              trainings
                ? trainings.results.length < trainings.count && !isLoading
                : !isLoading
            }
            loader={<LoadingBlock message={t('Loading')} key="loading" />}
            useWindow={false}
          >
            <div id="trainingsListId">
              <Table
                columns={columns}
                data={trainings ? trainings.results : []}
                alternateRows
                stickyHeader
                stickedHeader={stickedHeader}
                selectedRow={
                  trainings
                    ? trainings.results.findIndex(
                        (tr) => tr.id === selectedTrainingId
                      )
                    : -1
                }
                onRowClick={(tr) => {
                  history.push({
                    hash: tr.id.toString(),
                    state: {
                      trainingId: tr.id,
                    },
                  })
                  setTraining(dispatch, dissoc('actions', tr))
                }}
                isEmpty={
                  trainings &&
                  trainings.count === 0 &&
                  trainings.results.length <= 0
                }
                emptyMessage={t(
                  `There's nothing here. Try creating a new {{ itemName }}.`,
                  { itemName: t('training') }
                )}
              />
              {isLoading && (
                <LoadingBlock message={t('Loading')} key="loading" />
              )}
              {trainings &&
                !isLoading &&
                trainings.results.length >= trainings.count &&
                trainings.count > 0 && (
                  <div className={classes.endOfList}>
                    {t('All {{count}} records loaded', {
                      count: trainings.count,
                    })}
                  </div>
                )}
            </div>
          </InfiniteScroll>
        )}
      </InfiniteListWrapper>

      {deleteConfirmationModal && selectedTraining && (
        <DeleteConfirmationModal
          onCloseRequest={() => setDeleteConfirmationModal(false)}
          onDeleteRequest={() =>
            deleteTraining(dispatch, selectedTraining.id, setDeleteSubmitting)
          }
        />
      )}

      {downloadConfirmationModal && (
        <DownloadConfirmationModal
          onCloseRequest={() => setDownloadConfirmationModal(false)}
          onDownloadRequest={() => {
            if (selectedTraining) doDownloadTrainingFile(selectedTraining.id)
          }}
        />
      )}

      {trainingsUpdateModal && selectedTraining && (
        <TrainingsUpdate
          onClose={() => setTrainingsUpdateModal(false)}
          onSuccess={() => {
            setDetailsRefreshTrigger?.((currentKey) => currentKey + 1)
            toast.success(t('The training has been edited'))
            setTrainingsUpdateModal(false)
          }}
          trainingId={selectedTraining.id}
          initialValues={{
            name: selectedTraining.name,
            notes: selectedTraining.notes || '',
          }}
        />
      )}
      {htmlHelperModal && product && (
        <HtmlModal
          productType={product.type}
          onCloseRequest={() => setHtmlHelperModal(false)}
        />
      )}
      {precisionAlertModal && (
        <PrecisionAlertModal
          action="download"
          handleClose={() => {
            setPrecisionAlertModal(false)
          }}
          setParentPreference={(newPreference: boolean) => {
            setPreferenceShowAlert(newPreference)
          }}
        />
      )}
    </>
  )
}

export default TrainingList
